import React, { useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';

import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  OutlinedInput,
  Select,
  Stack,
  Typography
} from '@mui/material';

import { CloseOutlined, KeyboardArrowDown } from '@mui/icons-material';

import {
  dialogHeaderStyles,
  menuProps,
  textFieldSx
} from '../../../../../Common/components/Styles/InputStyles';
import CommonDialogActions from '../../../../../Common/components/CommonDialogActions';

const initialForm = {
  lastName: '',
  firstName: '',
  countryCode: '+1',
  mobile: '',
  email: '',
  confirmEmail: ''
};

const SendInvite = ({ open, initialValue, onClose, onSubmit }) => {
  const [form, setForm] = useState(initialForm);

  useEffect(() => {
    if (!open) return;

    setForm({
      ...initialForm,
      ...initialValue
    });
  }, [open, initialValue]);

  const handleChange = field => event => {
    setForm(previous => ({
      ...previous,
      [field]: event.target.value
    }));
  };

  const isValid = useMemo(() => {
    const email = form.email.trim();
    const confirmEmail = form.confirmEmail.trim();

    return Boolean(
      form.lastName.trim() &&
        form.firstName.trim() &&
        form.countryCode &&
        form.mobile.trim() &&
        email &&
        confirmEmail &&
        email === confirmEmail
    );
  }, [form]);

  const handleSubmit = () => {
    if (!isValid) return;

    onSubmit({
      lastName: form.lastName.trim(),
      firstName: form.firstName.trim(),
      countryCode: form.countryCode,
      mobile: form.mobile.trim(),
      email: form.email.trim(),
      confirmEmail: form.confirmEmail.trim()
    });
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="xs"
      aria-labelledby="invite-patient-title"
      PaperProps={{
        sx: {
          borderRadius: '6px'
        }
      }}>
      <DialogTitle sx={dialogHeaderStyles.dialogheader}>
        <Box>
          <Typography sx={dialogHeaderStyles.title}>
            Invite to Patient Portal &amp; App
          </Typography>
          <Typography sx={dialogHeaderStyles.subtitle}>
            Send an invitation to activate the EHRZip Connect patient portal and
            mobile app
          </Typography>
        </Box>

        <IconButton
          aria-label="Close"
          onClick={onClose}
          size="small"
          sx={dialogHeaderStyles.dialogheaderclose}>
          <CloseOutlined sx={{ fontSize: 22 }} />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ px: 2, my: 2 }}>
        <Stack spacing={1.8} sx={{ pt: 1 }}>
          <FormControl fullWidth size="small" sx={{ ...textFieldSx }}>
            <InputLabel htmlFor="invite-last-name">Last name</InputLabel>

            <OutlinedInput
              id="invite-last-name"
              label="Last name"
              value={form.lastName}
              onChange={handleChange('lastName')}
            />
          </FormControl>

          <FormControl fullWidth size="small" sx={textFieldSx}>
            <InputLabel htmlFor="invite-first-name">First name</InputLabel>

            <OutlinedInput
              id="invite-first-name"
              label="First name"
              value={form.firstName}
              onChange={handleChange('firstName')}
            />
          </FormControl>
          <Stack direction="row" spacing={1}>
            <FormControl fullWidth size="small" sx={{...textFieldSx,width:'35%'}}>
            <InputLabel id="invite-country-code-label">Country code</InputLabel>

            <Select
              labelId="invite-country-code-label"
              id="invite-country-code"
              label="Country code"
              value={form.countryCode}
              onChange={handleChange('countryCode')}
              IconComponent={KeyboardArrowDown}
              MenuProps={menuProps}>
              <MenuItem value="+1">+1</MenuItem>
              <MenuItem value="+91">+91</MenuItem>
              <MenuItem value="+415">+415</MenuItem>
              <MenuItem value="+61">+61</MenuItem>
            </Select>
          </FormControl>

            <FormControl fullWidth size="small" sx={{...textFieldSx,width:'80%'}}>
              <InputLabel htmlFor="mobile">Mobile Number</InputLabel>
              <OutlinedInput
                id="mobile"
                label="Mobile Number"
                value={form.mobile}
                onChange={handleChange('mobile')}
              />
            </FormControl>
          </Stack>

          <FormControl fullWidth size="small" sx={textFieldSx}>
            <InputLabel htmlFor="invite-email">Email address</InputLabel>

            <OutlinedInput
              id="invite-email"
              type="email"
              label="Email address"
              value={form.email}
              onChange={handleChange('email')}
            />
          </FormControl>

          <FormControl fullWidth size="small" sx={textFieldSx}>
            <InputLabel htmlFor="invite-confirm-email">
              Confirm email address
            </InputLabel>

            <OutlinedInput
              id="invite-confirm-email"
              type="email"
              label="Confirm email address"
              value={form.confirmEmail}
              onChange={handleChange('confirmEmail')}
            />
          </FormControl>
        </Stack>
      </DialogContent>
      <CommonDialogActions
        onCancel={onClose}
        onSubmit={handleSubmit}
        submitText="Send Invite"
        disabled={!isValid}
      />
    </Dialog>
  );
};

SendInvite.propTypes = {
  open: PropTypes.bool,
  initialValue: PropTypes.shape({
    lastName: PropTypes.string,
    firstName: PropTypes.string,
    countryCode: PropTypes.string,
    mobile: PropTypes.string,
    email: PropTypes.string,
    confirmEmail: PropTypes.string
  }),
  onClose: PropTypes.func,
  onSubmit: PropTypes.func
};

SendInvite.defaultProps = {
  open: false,
  initialValue: null,
  onClose: () => {},
  onSubmit: () => {}
};

export default SendInvite;
